export class FetchTimeoutError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number, message?: string) {
    super(message || `Request timed out after ${timeoutMs}ms`);
    this.name = "FetchTimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

const TIMEOUT_ABORT_REASON = "TIMEOUT";

export function isFetchTimeoutError(error: unknown): error is FetchTimeoutError {
  return error instanceof FetchTimeoutError;
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs?: number
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0) {
    return fetch(input, init);
  }

  const timeoutController = new AbortController();
  const upstreamSignal = init.signal;
  const combinedController = new AbortController();

  const onUpstreamAbort = () => {
    combinedController.abort(upstreamSignal?.reason);
  };

  const onTimeoutAbort = () => {
    combinedController.abort(TIMEOUT_ABORT_REASON);
  };

  if (upstreamSignal?.aborted) {
    onUpstreamAbort();
  } else {
    upstreamSignal?.addEventListener("abort", onUpstreamAbort, { once: true });
  }
  timeoutController.signal.addEventListener("abort", onTimeoutAbort, { once: true });

  const timeoutId = globalThis.setTimeout(
    () => timeoutController.abort(TIMEOUT_ABORT_REASON),
    timeoutMs
  );

  try {
    return await fetch(input, {
      ...init,
      signal: combinedController.signal,
    });
  } catch (error) {
    if (
      error instanceof DOMException &&
      error.name === "AbortError" &&
      timeoutController.signal.aborted
    ) {
      throw new FetchTimeoutError(timeoutMs);
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
    upstreamSignal?.removeEventListener("abort", onUpstreamAbort);
    timeoutController.signal.removeEventListener("abort", onTimeoutAbort);
  }
}
